import { useState, useEffect } from "react";
import { ArrowRight, ArrowLeft, Loader2 } from "lucide-react";
import { API_URL } from "@/lib/api";

interface EmpresaData {
  rnc: string;
  razonSocial: string;
  nombreComercial: string;
  direccion: string;
  provincia: string;
  municipio: string;
  actividad: string;
}

interface Step2Props {
  data: EmpresaData;
  onChange: (data: EmpresaData | ((prev: EmpresaData) => EmpresaData)) => void;
  onNext: () => void;
  onBack: () => void;
}

const PROVINCIAS = [
  "Distrito Nacional", "Santo Domingo", "Santiago", "San Cristóbal", "La Vega",
  "San Pedro de Macorís", "La Romana", "Puerto Plata", "Duarte", "Espaillat",
  "Monseñor Nouel", "La Altagracia", "El Seibo", "Hato Mayor", "Monte Plata",
  "Peravia", "Azua", "Barahona", "Bahoruco", "Independencia", "Pedernales",
];

const ACTIVIDADES = [
  "Comercio al por mayor", "Comercio al por menor", "Manufactura", "Servicios profesionales",
  "Construcción", "Transporte y logística", "Tecnología e informática", "Salud y farmacia",
  "Educación", "Turismo y hotelería", "Agricultura", "Exportación", "Importación", "Otro",
];

function validateRNC(rnc: string): boolean {
  const clean = rnc.replace(/[-\s]/g, "");
  return /^\d{9}$/.test(clean) || /^\d{11}$/.test(clean);
}

type RncLookup = "idle" | "loading" | "found" | "not_found" | "error";

export default function Step2Empresa({ data, onChange, onNext, onBack }: Step2Props) {
  const [errors, setErrors] = useState<Partial<EmpresaData>>({});
  const [rncLookup, setRncLookup] = useState<RncLookup>("idle");
  const [rncInfo, setRncInfo] = useState<string | null>(null);

  useEffect(() => {
    const cleanRnc = data.rnc.replace(/[-\s]/g, "");
    if (cleanRnc.length !== 9 && cleanRnc.length !== 11) {
      setRncInfo(null);
      setRncLookup("idle");
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setRncLookup("loading");
    setRncInfo(null);

    const timer = window.setTimeout(async () => {
      try {
        const response = await fetch(`${API_URL}/dgii/rnc/${cleanRnc}`, {
          signal: controller.signal,
        });
        if (cancelled) return;
        if (response.status === 404) {
          setRncInfo(null);
          setRncLookup("not_found");
          return;
        }
        if (!response.ok) {
          setRncInfo(null);
          setRncLookup("error");
          return;
        }
        const res = await response.json();
        if (cancelled) return;
        const razonSocial = (res.razon_social || res.nombre_comercial || "").trim();
        if (!razonSocial) {
          setRncInfo(null);
          setRncLookup("not_found");
          return;
        }
        setRncInfo(razonSocial);
        setRncLookup("found");
        onChange((prev) => ({
          ...prev,
          razonSocial,
          nombreComercial: res.nombre_comercial || prev.nombreComercial || "",
        }));
        setErrors((prev) => ({ ...prev, rnc: undefined, razonSocial: undefined }));
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) {
          return;
        }
        setRncInfo(null);
        setRncLookup("error");
      }
    }, 300);

    return () => {
      cancelled = true;
      controller.abort();
      window.clearTimeout(timer);
    };
  }, [data.rnc, onChange]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!validateRNC(data.rnc)) e.rnc = "El RNC debe tener 9 u 11 dígitos";
    if (!data.razonSocial.trim()) e.razonSocial = "La razón social es requerida";
    if (!data.direccion.trim()) e.direccion = "La dirección es requerida";
    if (!data.provincia) e.provincia = "Selecciona una provincia";
    if (!data.municipio.trim()) e.municipio = "El municipio es requerido";
    if (!data.actividad) e.actividad = "Selecciona una actividad";
    setErrors(e as Partial<EmpresaData>);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (validate()) onNext();
  };

  const inputClass = (field: keyof EmpresaData) =>
    `w-full border rounded-xl px-4 py-2.5 text-sm outline-none transition-colors ${
      errors[field] ? "border-red-300 bg-red-50" : "border-slate-200 focus:border-blue-700"
    }`;

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-bold text-slate-900 mb-1">Datos de la empresa</h2>
        <p className="text-sm text-slate-500">Información fiscal de la empresa emisora de e-CF.</p>
      </div>

      {/* RNC */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          RNC <span className="text-slate-400 text-xs">(Registro Nacional del Contribuyente)</span>
        </label>
        <div className="relative">
          <input
            type="text"
            value={data.rnc}
            onChange={(e) => onChange({ ...data, rnc: e.target.value })}
            placeholder="1-30-52765-3 o 001-0000000-0"
            maxLength={15}
            className={inputClass("rnc")}
          />
          {rncLookup === "loading" && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 size={16} className="animate-spin text-blue-800" />
            </span>
          )}
        </div>
        {errors.rnc && <p className="text-xs text-red-500 mt-1">{errors.rnc}</p>}
        {!errors.rnc && rncLookup === "loading" && (
          <p className="text-xs text-blue-600 mt-1">Buscando contribuyente en padrón DGII...</p>
        )}
        {!errors.rnc && rncLookup === "found" && rncInfo && (
          <p className="text-xs text-emerald-600 mt-1">✓ Encontrado: {rncInfo}</p>
        )}
        {!errors.rnc && rncLookup === "not_found" && (
          <p className="text-xs text-amber-600 mt-1">⚠️ RNC no encontrado en padrón DGII. Completa los datos manualmente.</p>
        )}
        {!errors.rnc && rncLookup === "error" && (
          <p className="text-xs text-amber-600 mt-1">⚠️ No se pudo consultar el padrón DGII. Completa los datos manualmente.</p>
        )}
      </div>

      {/* Razón social */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Razón social</label>
        <input
          type="text"
          value={data.razonSocial}
          onChange={(e) => onChange({ ...data, razonSocial: e.target.value })}
          placeholder="EMPRESA EJEMPLO, S.R.L."
          className={inputClass("razonSocial")}
        />
        {errors.razonSocial && <p className="text-xs text-red-500 mt-1">{errors.razonSocial}</p>}
      </div>

      {/* Nombre comercial */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">
          Nombre comercial <span className="text-slate-400 text-xs">(opcional)</span>
        </label>
        <input
          type="text"
          value={data.nombreComercial}
          onChange={(e) => onChange({ ...data, nombreComercial: e.target.value })}
          placeholder="Empresa Ejemplo"
          className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-700 transition-colors"
        />
      </div>

      {/* Dirección */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Dirección</label>
        <input
          type="text"
          value={data.direccion}
          onChange={(e) => onChange({ ...data, direccion: e.target.value })}
          placeholder="Calle 2-A #12-B, Edif. Asset Enterprise, 3er Nivel, El Millon"
          className={inputClass("direccion")}
        />
        {errors.direccion && <p className="text-xs text-red-500 mt-1">{errors.direccion}</p>}
      </div>

      {/* Provincia + Municipio */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Provincia</label>
          <select
            value={data.provincia}
            onChange={(e) => onChange({ ...data, provincia: e.target.value })}
            className={`${inputClass("provincia")} cursor-pointer`}
          >
            <option value="">Seleccionar...</option>
            {PROVINCIAS.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
          {errors.provincia && <p className="text-xs text-red-500 mt-1">{errors.provincia}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Municipio</label>
          <input
            type="text"
            value={data.municipio}
            onChange={(e) => onChange({ ...data, municipio: e.target.value })}
            placeholder="Santo Domingo de Guzmán"
            className={inputClass("municipio")}
          />
          {errors.municipio && <p className="text-xs text-red-500 mt-1">{errors.municipio}</p>}
        </div>
      </div>

      {/* Actividad económica */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Actividad económica</label>
        <select
          value={data.actividad}
          onChange={(e) => onChange({ ...data, actividad: e.target.value })}
          className={`${inputClass("actividad")} cursor-pointer`}
        >
          <option value="">Seleccionar actividad...</option>
          {ACTIVIDADES.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        {errors.actividad && <p className="text-xs text-red-500 mt-1">{errors.actividad}</p>}
      </div>

      {/* Actions */}
      <div className="flex gap-3 pt-2">
        <button
          onClick={onBack}
          className="flex items-center gap-2 border border-slate-200 text-slate-600 font-medium px-5 py-3 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <ArrowLeft size={16} />
          Atrás
        </button>
        <button
          onClick={handleNext}
          className="flex-1 flex items-center justify-center gap-2 bg-blue-800 text-white font-semibold py-3 rounded-xl hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
        >
          Continuar
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
}